import type { CURRENCY, TRIP_STATE } from '@allocab/allocab-commons-shared-types';
import { TripOutput } from '@allocab/allocab-passenger-shared-types';

import { assertNever } from './common';
import { dateDiff } from './date';
import { locale, t } from '../services/translation/translation';
import { TransportPickerType } from '../services/types';

export type TripStep =
  | 'BOOKING_CONFIRMED'
  | 'SEARCHING_DRIVER'
  | 'DRIVER_APPROACHING'
  | 'DRIVER_IN_PLACE'
  | 'ONDUTY'
  | 'DONE'
  | 'CANCELLED'
  | 'DRIVER_PREVIOUS_DROP_OFF';

export const isTransportTrip = (trip: TripOutput) =>
  trip.tripVars.tripType === 'FARETYPE_AIRPORT' || trip.tripVars.tripType === 'FARETYPE_STATION';

export const getStepForTrip = (trip: TripOutput): TripStep => {
  const {
    state,
    subState,
    resume: { departureAt },
  } = trip;
  if (state == 'INCOMING') {
    if (dateDiff(Date.now(), departureAt, 'minutes') <= -30) {
      return 'BOOKING_CONFIRMED';
    }

    if (subState === 'STANDBY_BEFORE_APPROACH_INCOMING') {
      if (trip.tripVars.flash) {
        return 'SEARCHING_DRIVER';
      } else {
        return 'BOOKING_CONFIRMED';
      }
    }

    if (subState === 'IN_APPROACH_INCOMING') {
      if (!trip.driver) {
        return 'SEARCHING_DRIVER';
      } else if (trip.driver.previousTripDropoffLat != null && trip.driver.previousTripDropoffLng != null) {
        return 'DRIVER_PREVIOUS_DROP_OFF';
      } else {
        return 'DRIVER_APPROACHING';
      }
    }

    if (subState === 'IN_PLACE_INCOMING') {
      if (!trip.driver) {
        return 'SEARCHING_DRIVER';
      } else {
        return 'DRIVER_IN_PLACE';
      }
    }
  }

  if (state === 'ONDUTY' || state === 'ONPAYMENT') {
    return 'ONDUTY';
  }

  if (state === 'DONE') {
    return 'DONE';
  }

  if (state === 'CANCELLED') {
    return 'CANCELLED';
  }

  return 'BOOKING_CONFIRMED';
};

export const getRefreshIntervalByStep = (step: TripStep) => {
  if (step === 'DONE' || step === 'CANCELLED') return false as const;

  if (
    step === 'SEARCHING_DRIVER' ||
    step === 'DRIVER_PREVIOUS_DROP_OFF' ||
    step === 'DRIVER_APPROACHING' ||
    step === 'DRIVER_IN_PLACE'
  ) {
    return 5000;
  }

  if (step === 'ONDUTY') {
    return 10000;
  }

  return 30000;
};

export const getRefreshInterval = (trip?: TripOutput | null) => {
  if (!trip) return false as const;

  const step = getStepForTrip(trip);

  return getRefreshIntervalByStep(step);
};
export const getCurrencySymbol = (currency: CURRENCY) => {
  switch (currency) {
    case 'EURO':
      return '€';
    case 'DOLLAR':
      return '$';
    case 'YEN':
      return '¥';
    default:
      assertNever(currency, '[getCurrencySymbol] Currency unknown');
      return '';
  }
};

export const formatPrice = (
  amount?: number,
  currency?: CURRENCY,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
) => {
  if (amount === undefined) return '';

  const currencySymbol = currency ? getCurrencySymbol(currency) : '€';

  return `${(amount / 100).toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  })}${currencySymbol}`;
};

export const getTripStatus = (trip: TripOutput) => {
  const step = getStepForTrip(trip);

  switch (step) {
    case 'BOOKING_CONFIRMED':
      return t('trip.status.booking-confirmed');
    case 'SEARCHING_DRIVER':
      return t('trip.status.searching-driver');
    case 'DRIVER_PREVIOUS_DROP_OFF':
      return t('trip.status.driver-previous-drop-off');
    case 'DRIVER_APPROACHING':
      return t('trip.status.driver-approaching');
    case 'DRIVER_IN_PLACE':
      return t('trip.status.driver-in-place');
    case 'ONDUTY':
      return t('trip.status.on-duty');
    case 'DONE':
      return t('trip.status.done');
    case 'CANCELLED':
      return t('trip.status.cancelled');
    default:
      assertNever(step, '[getTripStatus] Step unknown');
      return t('common.unknown');
  }
};

export const getTripStateName = (state: TRIP_STATE) => {
  switch (state) {
    case 'INCOMING':
      return t('trip.state.incoming');
    case 'ONPAYMENT':
    case 'ONDUTY':
      return t('trip.state.onduty');
    case 'DONE':
      return t('trip.state.done');
    case 'CANCELLED':
      return t('trip.state.cancelled');
    default:
      assertNever(state, '[getTripStatus] Step unknown');
      return t('common.unknown');
  }
};

export const getMinimalDelay = (type: TransportPickerType | null, minimalDelay?: number) =>
  minimalDelay != null ? Math.ceil(minimalDelay / 5) * 5 : type === 'train' ? 10 : 20;
